import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { BRAND_LOGO, BRAND_NAME } from "../lib/brand";

/**
 * Public per-tenant booking page.
 *
 * URL: /book/:slug — no auth required.
 * Three-step flow: pick service → pick date+time → contact details.
 * Submits to POST /api/public/tenants/:slug/booking-requests, which lands
 * the request in the trainer's /admin/booking-requests inbox.
 */
export default function PublicBooking() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [services, setServices] = useState([]);
  const [notFound, setNotFound] = useState(false);

  // wizard state
  const [step, setStep] = useState(1);
  const [picked, setPicked] = useState(null); // service object
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [form, setForm] = useState({ name: "", email: "", phone: "", dog_name: "", suburb: "", notes: "", hp: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/tenants/${slug}`);
        if (!alive) return;
        setTenant(data.tenant);
        setServices(data.services || []);
      } catch (e) {
        if (!alive) return;
        if (e.response?.status === 404) setNotFound(true);
        else toast.error("Couldn't load this booking page");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const isoStartsAt = useMemo(() => {
    if (!date || !time) return "";
    return `${date}T${time}:00`; // tenant timezone is implied (admin can adjust on approve)
  }, [date, time]);

  const minDate = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); // earliest = tomorrow
    return d.toISOString().slice(0, 10);
  }, []);

  const submit = async () => {
    if (!picked || !isoStartsAt || !form.name.trim() || !form.email.trim()) {
      toast.error("Pick a service, time, and fill name + email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/public/tenants/${slug}/booking-requests`, {
        service_slug: picked.slug,
        starts_at: isoStartsAt,
        duration_min: Number(picked.duration_min || picked.duration || 60),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        dog_name: form.dog_name.trim(),
        suburb: form.suburb.trim(),
        notes: form.notes.trim(),
        hp: form.hp,
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit your request — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-stone-500 text-sm">Loading…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-14 mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-stone-900 mb-2">Booking page not found</h1>
          <p className="text-stone-600 mb-6">Double-check the link or ask your trainer to share it again.</p>
          <Link to="/" className="text-[#1F9577] font-semibold hover:underline">Back to {BRAND_NAME}</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-12" data-testid="public-booking-success">
        <div className="max-w-md text-center bg-white border border-stone-200 rounded-2xl p-10 shadow-sm">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mb-5">
            <span className="text-3xl">✓</span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 mb-3">Request sent</h1>
          <p className="text-stone-600 mb-6">
            {tenant.business_name} will confirm your booking by email shortly. Check your inbox (and spam folder, just in case).
          </p>
          <p className="text-xs text-stone-400">Powered by <strong>{BRAND_NAME}</strong></p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#33C095] flex items-center justify-center text-white font-bold text-lg">
              {tenant.business_name?.[0]?.toUpperCase() || "P"}
            </div>
            <div>
              <div className="font-heading font-bold text-stone-900" data-testid="public-booking-business-name">{tenant.business_name}</div>
              <div className="text-xs text-stone-500">Book a session</div>
            </div>
          </div>
          <div className="text-xs text-stone-400 hidden sm:block">
            Powered by <Link to="/" className="font-semibold text-stone-600 hover:text-[#1F9577]">{BRAND_NAME}</Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Stepper */}
        <ol className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-400 mb-8">
          <li className={step >= 1 ? "text-[#1F9577]" : ""}>1 · Service</li>
          <li>›</li>
          <li className={step >= 2 ? "text-[#1F9577]" : ""}>2 · Time</li>
          <li>›</li>
          <li className={step >= 3 ? "text-[#1F9577]" : ""}>3 · Your details</li>
        </ol>

        {/* Step 1 — service */}
        {step === 1 && (
          <section data-testid="public-booking-step-services">
            <h2 className="font-heading text-2xl font-bold text-stone-900 mb-2">What kind of session?</h2>
            <p className="text-stone-600 mb-6">Pick the service you&apos;re after.</p>
            {services.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-5 text-sm">
                {tenant.business_name} hasn&apos;t enabled any services for public booking yet. Send them an email or DM instead — {tenant.contact_email && (<><a href={`mailto:${tenant.contact_email}`} className="font-semibold underline">{tenant.contact_email}</a></>)}.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((s) => {
                  const dur = s.duration_min || s.duration;
                  return (
                    <button
                      key={s.slug}
                      data-testid={`public-booking-service-${s.slug}`}
                      onClick={() => { setPicked(s); setStep(2); }}
                      className="text-left bg-white border-2 border-stone-200 hover:border-[#33C095] hover:shadow-sm rounded-xl p-5 transition"
                    >
                      <div className="font-bold text-stone-900 mb-1">{s.title}</div>
                      {s.headline && <div className="text-sm text-stone-600 mb-3">{s.headline}</div>}
                      <div className="flex items-center gap-3 text-xs text-stone-500">
                        {dur ? <span>⏱ {dur} min</span> : null}
                        {s.price ? <span>· ${Number(s.price).toFixed(0)} AUD</span> : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Step 2 — date + time */}
        {step === 2 && picked && (
          <section data-testid="public-booking-step-time">
            <button onClick={() => setStep(1)} className="text-xs text-stone-500 hover:text-stone-700 mb-4">← Change service</button>
            <h2 className="font-heading text-2xl font-bold text-stone-900 mb-2">When works for you?</h2>
            <p className="text-stone-600 mb-6">
              {picked.title} · {picked.duration_min || picked.duration || 60} min — pick a date and time. Your trainer will confirm by email.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-md">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Preferred date</label>
                <input
                  type="date" min={minDate} value={date} onChange={(e) => setDate(e.target.value)}
                  data-testid="public-booking-date"
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Preferred time</label>
                <input
                  type="time" value={time} onChange={(e) => setTime(e.target.value)}
                  data-testid="public-booking-time"
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30"
                />
              </div>
            </div>
            <div className="mt-8">
              <button
                disabled={!date}
                onClick={() => setStep(3)}
                data-testid="public-booking-time-next"
                className="rounded-lg bg-[#33C095] hover:bg-[#1F9577] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5"
              >
                Continue →
              </button>
            </div>
          </section>
        )}

        {/* Step 3 — contact details */}
        {step === 3 && picked && (
          <section data-testid="public-booking-step-contact">
            <button onClick={() => setStep(2)} className="text-xs text-stone-500 hover:text-stone-700 mb-4">← Change time</button>
            <h2 className="font-heading text-2xl font-bold text-stone-900 mb-2">Your details</h2>
            <p className="text-stone-600 mb-6">So {tenant.business_name} can get in touch.</p>

            {/* Summary card */}
            <div className="bg-white border border-stone-200 rounded-xl p-4 mb-6 text-sm">
              <div className="text-stone-500 text-xs uppercase tracking-wider mb-2">You&apos;re requesting</div>
              <div className="font-bold text-stone-900">{picked.title}</div>
              <div className="text-stone-600 mt-1">
                {new Date(isoStartsAt).toLocaleDateString("en-AU", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })} at {time}
                {" · "}{picked.duration_min || picked.duration || 60} min
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Your name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="public-booking-name"
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="public-booking-email"
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Phone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="public-booking-phone"
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Dog&apos;s name</label>
                <input value={form.dog_name} onChange={(e) => setForm({ ...form, dog_name: e.target.value })}
                  data-testid="public-booking-dog"
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-stone-700 mb-1">Suburb</label>
                <input value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })}
                  data-testid="public-booking-suburb"
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-stone-700 mb-1">Anything we should know?</label>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="public-booking-notes"
                  placeholder="E.g. my dog pulls on lead, or I'm specifically interested in loose-lead training…"
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30" />
              </div>
              {/* Honeypot — hidden from humans, bots fill it. */}
              <input
                type="text" tabIndex={-1} autoComplete="off"
                value={form.hp} onChange={(e) => setForm({ ...form, hp: e.target.value })}
                style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
                aria-hidden="true"
              />
            </div>

            <button
              onClick={submit}
              disabled={submitting}
              data-testid="public-booking-submit"
              className="mt-8 rounded-lg bg-[#33C095] hover:bg-[#1F9577] disabled:opacity-50 text-white font-semibold px-8 py-3"
            >
              {submitting ? "Sending…" : "Send booking request"}
            </button>
            <p className="text-xs text-stone-400 mt-3">By submitting, you agree to be contacted by {tenant.business_name} about your booking.</p>
          </section>
        )}
      </main>

      <footer className="mt-16 py-8 border-t border-stone-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center text-xs text-stone-400">
          Powered by <Link to="/" className="font-semibold text-stone-600 hover:text-[#1F9577]">{BRAND_NAME}</Link>
          {" · "}
          <Link to="/get-started" className="hover:text-[#1F9577]">Run your own dog-training business</Link>
        </div>
      </footer>
    </div>
  );
}
